/**
 * Task creation/edit modal.
 * Supports title, due date/time, priority, recurrence, description.
 */

import { useState, useEffect, type FormEvent } from 'react';
import type { Task, CreateTaskInput } from '@anna-calendar/shared';
import { Modal } from './Modal';
import { formatDateISO } from '../utils/dates';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateTaskInput) => void;
  task?: Task | null;
  defaultDate?: string;
}

const PRIORITIES = [
  { value: 0, label: 'Low' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'High' },
];

const RECURRENCE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'FREQ=DAILY', label: 'Daily' },
  { value: 'FREQ=WEEKLY', label: 'Weekly' },
  { value: 'FREQ=MONTHLY', label: 'Monthly' },
];

export function TaskModal({ isOpen, onClose, onSave, task, defaultDate }: TaskModalProps) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState(0);
  const [recurrence, setRecurrence] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDueDate(task.due_date || '');
      setDueTime(task.due_time || '');
      setPriority(task.priority);
      setRecurrence(task.recurrence_rule || '');
      setDescription(task.description || '');
    } else {
      setTitle('');
      setDueDate(defaultDate || formatDateISO(new Date()));
      setDueTime('');
      setPriority(0);
      setRecurrence('');
      setDescription('');
    }
  }, [task, isOpen, defaultDate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    onSave({
      title: title.trim(),
      description: description.trim() || null,
      due_date: dueDate || null,
      due_time: dueTime || null,
      priority,
      completed: task?.completed ?? 0,
      recurrence_rule: recurrence || null,
      reminder_minutes: null,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={task ? 'Edit Task' : 'New Task'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            required
            className="w-full px-3 py-3 rounded-lg text-base outline-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          />
        </div>

        {/* Due date & time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Due date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-3 rounded-lg text-base outline-none"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                minHeight: '44px',
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Due time
            </label>
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className="w-full px-3 py-3 rounded-lg text-base outline-none"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                minHeight: '44px',
              }}
            />
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Priority
          </label>
          <div className="flex gap-2">
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPriority(p.value)}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  minHeight: '44px',
                  backgroundColor: priority === p.value ? 'var(--accent)' : 'var(--bg-tertiary)',
                  color: priority === p.value ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${priority === p.value ? 'var(--accent)' : 'var(--border-color)'}`,
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recurrence */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Recurrence
          </label>
          <select
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value)}
            className="w-full px-3 py-3 rounded-lg text-base outline-none appearance-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              minHeight: '44px',
            }}
          >
            {RECURRENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={3}
            className="w-full px-3 py-3 rounded-lg text-base outline-none resize-none"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            }}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!title.trim()}
          className="w-full py-3 rounded-lg text-base font-semibold transition-opacity"
          style={{
            backgroundColor: 'var(--accent)',
            color: '#fff',
            opacity: !title.trim() ? 0.5 : 1,
            minHeight: '44px',
          }}
        >
          {task ? 'Save Changes' : 'Create Task'}
        </button>
      </form>
    </Modal>
  );
}
